'use client';

import { useTransition, type SyntheticEvent } from 'react';
import { useRouter } from 'next/navigation';
import { CART_DATA_PRIVACY_STATUS, useCartCheckoutStore, useCartStore } from 'stores/cart';
import { clearExpressDesignerStorage } from 'stores/express-designer';
import { buildJentisPayloadData, redirectToWithPageReload } from 'utils';
import { createOrderAction } from '../../actions/checkout/createOrderAction';
import { trackOrderButtonClick, trackOrderSuccess } from '../../analytics/checkout';
import { ORDER_SUCCESS_ROUTE } from '../../checkout/constants';

interface UseCheckoutOrderOptions {
  /** DK with a legal sentence: the customer must tick the sentence before ordering. */
  isDataPrivacyRequired: boolean;
  constants: Record<string, string>;
  /** Key of `currentPayment` (`invoice`, `gui_cc`, ...), not its `type`; the action of the success event. */
  paymentMethod: string | null;
  /** Route shown when PSI rejects the order. */
  errorRoute: string;
}

/**
 * Busy-guarded order click; the cart store shares the busy flag between the desktop and the mobile order button.
 * A payment form in the response means redirect to the provider, otherwise the order is final. The cart stays busy
 * through the hard navigation, otherwise a second click could create a second order.
 */
export function useCheckoutOrder({
  isDataPrivacyRequired,
  constants,
  paymentMethod,
  errorRoute
}: UseCheckoutOrderOptions) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const isCartBusy = useCartStore(store => store.isBusy);
  const setCartBusy = useCartStore(store => store.setCartBusy);
  const dataPrivacyStatus = useCartCheckoutStore(store => store.dataPrivacyStatus);
  const rejectDataPrivacy = useCartCheckoutStore(store => store.rejectDataPrivacy);
  const isBusy = isPending || isCartBusy;

  const showOrderError = () => {
    setCartBusy(false);
    router.push(errorRoute);
  };

  /**
   * Creates the order via PSI; the response decides the route, not the payment type.
   * A `redirectUrl` (PayPal, Computop) goes to the provider, otherwise to the success page of `orderId`.
   * Busy is released only on failure (error route), so a second click can't race the hard navigation.
   */
  const createOrder = async () => {
    try {
      const result = await createOrderAction({ constants, jentis: buildJentisPayloadData() });

      if (!result.success) {
        showOrderError();

        return;
      }

      // The order exists now (redirect = open order, direct = final), so the express products are no longer "added".
      clearExpressDesignerStorage();

      // Provider redirects fire no success event, like Gatsby.
      if (result.redirectUrl) {
        redirectToWithPageReload(result.redirectUrl);

        return;
      }

      // Tracked before the navigation, the page is gone afterwards.
      if (paymentMethod) {
        trackOrderSuccess(paymentMethod, result.orderId);
      }

      redirectToWithPageReload(`${ORDER_SUCCESS_ROUTE}${result.orderId}`);
    } catch {
      showOrderError();
    }
  };

  const handleOrderClick = (event: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();

    if (isBusy) {
      return;
    }

    if (isDataPrivacyRequired && dataPrivacyStatus !== CART_DATA_PRIVACY_STATUS.ACCEPTED) {
      rejectDataPrivacy();

      return;
    }

    trackOrderButtonClick();

    startTransition(async () => {
      setCartBusy(true);
      await createOrder();
    });
  };

  return { isBusy, handleOrderClick };
}
